// File: frontend/src/App.js

import { useState, useMemo, useEffect, useRef, useCallback, Profiler } from 'react';
import { fetchWithRetry } from './utils/fetchWithRetry';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { HomePageWrapper, DownloadPageWrapper, InstallPageWrapper } from './components/WebOnlyPages';
import AuthScreen from './components/AuthScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import ConfirmEmailScreen from './components/ConfirmEmailScreen';
import ConfirmEmailPendingScreen from './components/ConfirmEmailPendingScreen';
import AdminDashboard from './components/AdminDashboard';
import LoadingScreen from './components/LoadingScreen';
import EnhancedWeekView from './components/EnhancedWeekView';
import EnhancedSettingsModal from './components/EnhancedSettingsModal';
import StarterHabitsModal from './components/StarterHabitsModal';
import YearProgressDots from './components/YearProgressDots';
import YearViewModal from './components/YearViewModal';
import WeekProgress from './components/WeekProgress';
import DebugModal from './components/DebugModal';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import DeleteAccountPage from './components/DeleteAccountPage';
import SupportPage from './components/SupportPage';
import SupportAgentInbox from './components/SupportAgentInbox';
import motivationalMessages from './motivationalMessages';

import { logger } from './utils/logger';
import { goToApp } from './utils/goToApp';
import { recordCommit } from './utils/perfProbe';
import { LayoutProvider, useLayout } from './context/LayoutContext';
import { AnimationProvider } from './context/AnimationContext';
import { ThemeProvider } from './context/ThemeContext';
import { ConnectivityProvider } from './context/ConnectivityContext';
import { ToastProvider } from './context/ToastContext';
import ConnectivityToastBridge from './components/ConnectivityToastBridge';
import ScreenDimOverlay from './components/ScreenDimOverlay';
import { applyStoredVisibility as applyStatusBarVisibility } from './utils/statusBar';
import { getApiUrl } from './utils/apiHelpers';
import { getWeekDates } from './utils/dateHelpers';
import { useAuth } from './hooks/useAuth';
import { useHabits } from './hooks/useHabits';
import { useCompletions } from './hooks/useCompletions';
import { useSickWeeks } from './hooks/useSickWeeks';
import { useWeekNavigation } from './hooks/useWeekNavigation';
import { useDisplayControl } from './hooks/useDisplayControl';
import AppLayout from './components/app/AppLayout';
import ProtectedRoute from './components/app/ProtectedRoute';

// First-run onboarding flag — set on signup, consumed by AppContent to show
// the starter-habits modal once. Also gated on `habits.length === 0` so a
// returning Google user with existing habits won't see the modal even if the
// signup path set the flag.
const ONBOARDING_FLAG = 'steadily_needs_onboarding';

// Performance profiler callback - logs slow renders (>16ms = more than 1 frame).
// Uses debug level (local console only, not forwarded to backend) so this stays
// noise-free even if it fires often during dev work.
const onRenderCallback = (id, phase, actualDuration) => {
  recordCommit(id, phase, actualDuration);
  if (actualDuration > 16) {
    logger.debug('slow render', { id, phase, ms: +actualDuration.toFixed(1) });
  }
};

// Main App component
function AppContent({ user, isLoading, logout }) {
  const { currentLayout, config, proportionalLayout, updateHabitCount } = useLayout();
  const navigate = useNavigate();
  const apiUrl = getApiUrl();

  // Local UI state
  const [showSettings, setShowSettings] = useState(false);
  const [showYearView, setShowYearView] = useState(false);
  const [showStarterModal, setShowStarterModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Custom hooks
  const { habits, fetchHabits, addHabit, updateHabit, deleteHabit } = useHabits(apiUrl, user);

  // First-run onboarding: open the starter-habits modal once for a freshly
  // signed-up user with no habits. The flag is set by LoginPage on register /
  // Google auth. Gating on habits.length === 0 protects against the IndexedDB
  // hydration race and any returning-user edge case.
  useEffect(() => {
    if (localStorage.getItem(ONBOARDING_FLAG) === '1' && habits.length === 0) {
      setShowStarterModal(true);
    }
  }, [habits.length]);

  const dismissStarterModal = () => {
    localStorage.removeItem(ONBOARDING_FLAG);
    setShowStarterModal(false);
  };

  // Sync habit count into the adaptive layout system
  useEffect(() => {
    updateHabitCount(habits.length);
  }, [habits.length, updateHabitCount]);

  // Week navigation (needs to be created before other hooks that depend on it).
  // We use a ref to break the circular dependency: useCompletions needs
  // currentWeekStart from this hook, but useWeekNavigation also wants to call
  // fetchCompletions from useCompletions. The ref gets populated below and
  // useWeekNavigation reads through it at call time.
  const fetchCompletionsRef = useRef(null);
  const weekNav = useWeekNavigation(fetchCompletionsRef);
  const { currentWeekStart, selectedWeekStart, goToPreviousWeek, goToNextWeek, goToCurrentWeek } = weekNav;

  // Sick weeks (for marking habits as resting) - needs to be before completions for confetti check
  const sickWeeksHook = useSickWeeks(apiUrl, user, currentWeekStart);
  const { sickWeeks, toggleSickWeek } = sickWeeksHook;

  // Completions (depends on currentWeekStart and sickWeeks for confetti check)
  const completionsHook = useCompletions(apiUrl, user, habits, currentWeekStart, sickWeeks);
  const { completions, streaks, showConfetti, fetchCompletions, toggleCompletion } = completionsHook;

  fetchCompletionsRef.current = fetchCompletions;

  // Display control and refresh logic
  useDisplayControl(
    user,
    currentWeekStart,
    weekNav.setCurrentWeekStart,
    selectedWeekStart,
    weekNav.setSelectedWeekStart,
    fetchCompletions,
    fetchHabits
  );

  // Memoize weekDates to prevent unnecessary re-renders and animation jank in EnhancedWeekView
  const weekDates = useMemo(() => selectedWeekStart ? getWeekDates(selectedWeekStart) : [], [selectedWeekStart]);

  // Year view handler
  const handleYearClick = () => {
    setSelectedYear(new Date().getFullYear());
    setShowYearView(true);
  };

  // Logout handler
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };



  // Loading state
  const motivationalMessage = useMemo(() => {
    return motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
  }, []);

  if (isLoading || !currentWeekStart) {
    return <LoadingScreen />;
  }

  return (
    <Profiler id="AppLayout" onRender={onRenderCallback}>
    <AppLayout showConfetti={showConfetti}>
      {/* Main Container - Enforce Full Height & No Scroll */}
      <div className={`relative z-10 w-full h-full max-w-[125rem] mx-auto flex flex-col overflow-hidden ${config.spacing.container} ${config.spacing.componentGap}`}>

        {/* Header Section */}
        {config.components.header.visible && (
          config.components.header.variant === 'minimal' ? (
            // Minimal Header (Mobile/Compact).
            // Deliberately simple: solid opaque background, no backdrop-blur, no
            // `tracking-tight`. Portrait gets breathing room (py-2, text-xl, size-22
            // gear), landscape shrinks aggressively (py-0.5, text-base, size-18 gear,
            // smaller logo) because on a phone in landscape the viewport is only
            // ~411 CSS pixels tall — every pixel of header chrome is worth two.
            <header className="flex-shrink-0 px-3 py-2 landscape:py-0.5 flex items-center justify-between bg-[var(--color-paper-warm)] border-b border-ink/10 relative z-50">
              <div className="flex items-center space-x-3 landscape:space-x-2">
                <div className="w-8 h-8 landscape:w-6 landscape:h-6 flex items-center justify-center bg-[var(--color-paper-bg)] rounded-lg shadow-sm border border-[var(--color-ink-subtle)]/20 p-0.5 flex-shrink-0">
                  <img src="/android-chrome-192x192.png" alt="Logo" className="w-full h-full object-contain rounded-md" />
                </div>
                <span className="font-serif font-bold text-[var(--color-ink-main)] text-xl landscape:text-base whitespace-nowrap">Steadily.me</span>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 landscape:p-1.5 rounded-full hover:bg-[var(--color-ink-subtle)]/10 text-[var(--color-ink-light)] transition-colors flex-shrink-0"
                aria-label="Settings"
              >
                <Settings className="w-[22px] h-[22px] landscape:w-[18px] landscape:h-[18px]" />
              </button>
            </header>
          ) : (
            // Standard/Expanded Header
            <header className="flex-shrink-0 px-6 py-4 flex items-center justify-between bg-[var(--color-paper-warm)]/60 backdrop-blur-md border-b border-[var(--color-divider)] relative z-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center transition-transform hover:scale-110 duration-300 bg-[var(--color-surface-card)] rounded-xl shadow-sm border border-[var(--color-divider)] p-1">
                  <img src="/android-chrome-192x192.png" alt="Logo" className="w-full h-full object-contain rounded-lg" />
                </div>
                <span className="font-serif font-bold text-ink-main text-2xl tracking-tight">Steadily.me</span>
              </div>

              {/* Tagline — absolute-centered across the whole header so it stays
                  geometrically centered regardless of the left/right cluster widths.
                  Max-width + truncate keeps long taglines from bleeding into the
                  logo or the date/settings group; pointer-events-none makes sure
                  the text never eats a tap on the gear behind it. */}
              {/* Temporarily hidden — keep the wiring (config flag, useMemo,
                  motivationalMessages.js, hook state) intact so re-enabling
                  is a single-line revert of the `false &&` gate. */}
              {false && config.components.header.showMotivationalMessage && (
                <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[50%] text-center text-ink-light text-sm font-medium italic truncate pointer-events-none">
                  "{motivationalMessage}"
                </div>
              )}

              <div className="flex items-center space-x-3">
                {config.components.header.showDate && (
                  <div className="text-right hidden sm:block">
                    <div className="text-xs font-medium text-ink-light uppercase tracking-wider">Today</div>
                    <div className="text-sm font-semibold text-ink-main">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                  </div>
                )}

                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 rounded-full hover:bg-[var(--color-hover-fill)] text-ink-light transition-colors"
                  aria-label="Settings"
                >
                  <Settings size={18} />
                </button>
              </div>
            </header>
          )
        )}



        {/* Proportional Layout - Vertical Stack */}
        <main className="flex-1 min-h-0 flex flex-col space-y-2 relative z-0">
          {/* Year Progress Dots - 1/4 of screen */}
          <section
            className="min-h-0 overflow-hidden"
            style={{ flex: proportionalLayout.yearDotsFlex }}
          >
            <YearProgressDots
              year={new Date().getFullYear()}
              habits={habits}
              completions={completions}
              sickWeeks={sickWeeks}
              onClick={handleYearClick}
              onWeekSelect={weekNav.setSelectedWeekStart}
              layoutMode={currentLayout}
              userCreatedAt={user?.created_at}
              config={config.components.yearDots}
            />
          </section>

          {/* Current Week - 3/4 on phone, 2/4 on tablet+ */}
          <section
            className="min-h-0 overflow-hidden"
            style={{ flex: proportionalLayout.weekViewFlex }}
          >
            <EnhancedWeekView
              habits={habits}
              weekDates={weekDates}
              completions={completions}
              sickWeeks={sickWeeks}
              onToggle={toggleCompletion}
              onToggleSick={toggleSickWeek}
              isSmall={false}
              weekLabel={`Week of ${new Date(selectedWeekStart).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}`}
              streaks={streaks}
              isCurrentWeek={selectedWeekStart.getTime() === currentWeekStart.getTime()}
              layoutMode={currentLayout}
              userCreatedAt={user?.created_at}
              onPreviousWeek={goToPreviousWeek}
              onNextWeek={goToNextWeek}
              onCurrentWeek={goToCurrentWeek}
              canGoToNextWeek={selectedWeekStart.getTime() < currentWeekStart.getTime()}
              enableAnimations={true}
              config={config.components.weekView}
              onEmptySuggest={() => setShowStarterModal(true)}
              onEmptyAddOwn={() => setShowSettings(true)}
            />
          </section>

          {/* Stats Section - 1/4 on tablet+, hidden on phone */}
          {proportionalLayout.showStats && (
            <section
              className="min-h-0 overflow-y-auto"
              style={{ flex: proportionalLayout.statsFlex }}
            >
              <WeekProgress
                habits={habits}
                weekDates={getWeekDates(currentWeekStart)}
                completions={completions}
                sickWeeks={sickWeeks}
                currentWeekStart={currentWeekStart}
                layoutMode={currentLayout}
                userCreatedAt={user?.created_at}
                config={config.components.weekProgress}
              />
            </section>
          )}
        </main>

        {/* Settings Modal */}
        {
          showSettings && (
            <EnhancedSettingsModal
              habits={habits}
              onAdd={addHabit}
              onUpdate={updateHabit}
              onDelete={deleteHabit}
              onClose={() => setShowSettings(false)}
              apiUrl={apiUrl}
              onLogout={handleLogout}
            />
          )
        }

        {/* Year View Modal */}
        {
          showYearView && (
            <YearViewModal
              year={selectedYear}
              habits={habits}
              completions={completions}
              onClose={() => setShowYearView(false)}
            />
          )
        }

        {/* First-Run Starter Habits Modal */}
        {showStarterModal && (
          <StarterHabitsModal
            onAdd={addHabit}
            onClose={dismissStarterModal}
          />
        )}

        {/* Debug Modal - Type "DEBUG" to open */}
        <DebugModal />
      </div>
    </AppLayout>
    </Profiler>
  );
}

// Login page wrapper that handles auth state
function LoginPage({ onLogin }) {
  // Initialize state from getApiUrl, which checks localStorage
  const [apiUrl, setApiUrlState] = useState(getApiUrl());
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Where to land after login. ProtectedRoute sends users here with
  // ?next=<path> (e.g. /support); only internal paths are honored. A query
  // param rather than router state so it survives the Apple redirect flow.
  const rawNext = searchParams.get('next');
  const nextPath = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null;

  const finishLogin = useCallback((options = {}) => {
    if (nextPath) {
      navigate(nextPath, options);
    } else {
      goToApp(navigate, options);
    }
  }, [navigate, nextPath]);

  // If user is already logged in, redirect to their destination
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      finishLogin({ replace: true });
    }
  }, [finishLogin]);

  // Handler to update both state and localStorage
  const handleSetApiUrl = (newUrl) => {
    localStorage.setItem('dev_api_url', newUrl);
    setApiUrlState(newUrl);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await fetchWithRetry(`${apiUrl}/api/auth/${isLogin ? 'login' : 'register'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      // Email/password signup now returns 201 with pendingConfirmation:true
      // instead of a JWT. Swap UI to the "check your inbox" screen.
      if (!isLogin && res.ok && data.pendingConfirmation) {
        setPendingConfirmEmail(data.email || email);
        setPassword('');
        return;
      }

      // Login attempted with an unverified email/password account.
      if (isLogin && res.status === 403 && data.error === 'email_not_verified') {
        setPendingConfirmEmail(data.email || email);
        setPassword('');
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      localStorage.setItem('token', data.token);
      if (!isLogin) localStorage.setItem(ONBOARDING_FLAG, '1');
      setEmail('');
      setPassword('');

      if (onLogin) {
        await onLogin(data.token);
      }

      finishLogin();
    } catch (error) {
      setError(error.message);
    }
  };

  // Stable across renders. AuthScreen's web Google-button effect lists this
  // in its deps and calls renderButton() each time the ref changes — a new
  // function per keystroke caused the button to blink between "Continue as
  // <user>" and "Sign in with Google" while typing email/password.
  const handleGoogleLogin = useCallback(async (response) => {
    try {
      const res = await fetchWithRetry(`${apiUrl}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Google authentication failed');
      }

      localStorage.setItem('token', data.token);
      // Google could be a first-time signup or a returning login; we can't
      // tell from the response. Set the flag and rely on the habits.length
      // gate in AppContent to suppress the modal for returning users.
      localStorage.setItem(ONBOARDING_FLAG, '1');

      if (onLogin) {
        await onLogin(data.token);
      }

      finishLogin();
    } catch (error) {
      setError(error.message);
    }
  }, [apiUrl, onLogin, finishLogin]);

  const handleNativeGoogleLogin = async (result) => {
    try {
      const res = await fetchWithRetry(`${apiUrl}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idToken: result.idToken,
          isNative: true
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Google authentication failed');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem(ONBOARDING_FLAG, '1');

      if (onLogin) {
        await onLogin(data.token);
      }

      finishLogin();
    } catch (error) {
      setError(error.message);
    }
  };

  // Apple Sign-In. The `signInWithApple()` utility normalizes web / iOS native /
  // Electron into a single `{ identityToken, isNative }` shape, so unlike Google
  // we only need one handler.
  const handleAppleLogin = async (result) => {
    try {
      const res = await fetchWithRetry(`${apiUrl}/api/auth/apple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identityToken: result.identityToken,
          isNative: !!result.isNative,
          // One-time code (when the flow produced one) so the backend can
          // store a refresh token, revoked later at account deletion.
          ...(result.authorizationCode ? { authorizationCode: result.authorizationCode } : {}),
          ...(result.redirectUri ? { redirectUri: result.redirectUri } : {}),
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Apple authentication failed');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem(ONBOARDING_FLAG, '1');

      if (onLogin) {
        await onLogin(data.token);
      }

      finishLogin();
    } catch (error) {
      setError(error.message);
    }
  };

  if (pendingConfirmEmail) {
    return (
      <ConfirmEmailPendingScreen
        apiUrl={apiUrl}
        email={pendingConfirmEmail}
        onBackToLogin={() => {
          setPendingConfirmEmail('');
          setIsLogin(true);
          setError('');
        }}
      />
    );
  }

  return (
    <AuthScreen
      isLogin={isLogin}
      setIsLogin={setIsLogin}
      email={email}
      setEmail={setEmail}
      password={password}
      setPassword={setPassword}
      handleAuth={handleAuth}
      error={error}
      onGoogleLogin={handleGoogleLogin}
      onNativeGoogleLogin={handleNativeGoogleLogin}
      onAppleLogin={handleAppleLogin}
      apiUrl={apiUrl}
      setApiUrl={handleSetApiUrl}
    />
  );
}

// Wrap the app content with LayoutProvider and Router
function App() {
  const apiUrl = getApiUrl();
  const { user, isLoading, logout, fetchUserData } = useAuth(apiUrl);

  // Restore the status-bar visibility preference on launch — the native
  // plugin resets to "shown" otherwise.
  useEffect(() => {
    applyStatusBarVisibility();
  }, []);

  return (
    <BrowserRouter>
      <ConnectivityProvider>
        <ToastProvider>
          <ConnectivityToastBridge />
          <ThemeProvider>
          <AnimationProvider>
            <LayoutProvider>
              <ScreenDimOverlay />
              {/* On web these routes must ALSO be listed as rewrites in
                  public/serve.json — the production server only rewrites the
                  paths named there to index.html (no catch-all, because /app/
                  is the Godot web export served as static files). Marketing
                  pages that need their own <title> and link preview are also
                  listed in src/prerender.js. */}
              <Routes>
          <Route path="/" element={<HomePageWrapper />} />
          <Route path="/download" element={<DownloadPageWrapper />} />
          <Route path="/install" element={<InstallPageWrapper />} />
          <Route path="/login" element={<LoginPage onLogin={fetchUserData} />} />
          <Route path="/reset-password" element={<ResetPasswordScreen apiUrl={apiUrl} />} />
          <Route path="/confirm-email" element={<ConfirmEmailScreen apiUrl={apiUrl} onLogin={fetchUserData} />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/delete-account" element={<DeleteAccountPage user={user} isLoading={isLoading} />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute user={user} isLoading={isLoading}>
                <AdminDashboard user={user} logout={logout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute user={user} isLoading={isLoading}>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          {/* Single-segment on purpose: the build's relative asset paths
              (needed for file:// native builds) 404 under nested routes. */}
          <Route
            path="/support-agent"
            element={
              <ProtectedRoute user={user} isLoading={isLoading}>
                <SupportAgentInbox user={user} />
              </ProtectedRoute>
            }
          />
          {/* Native keeps the React tracker; on web /app/ is the Godot export
              (see goToApp). Inline env read so webpack folds the condition. */}
          {process.env.REACT_APP_IS_NATIVE === 'true' && (
            <Route
              path="/app"
              element={
                <ProtectedRoute user={user} isLoading={isLoading}>
                  <AppContent user={user} isLoading={isLoading} logout={logout} />
                </ProtectedRoute>
              }
            />
          )}
          <Route path="*" element={<HomePageWrapper />} />
        </Routes>
            </LayoutProvider>
          </AnimationProvider>
          </ThemeProvider>
        </ToastProvider>
      </ConnectivityProvider>
    </BrowserRouter>
  );
}

export default App;
